import useAppStore from "@/context/store";
import classNames from "classnames";
import { throttle } from "lodash";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import BoardCard from "../BoardCard";
import HBLoading from "../HBLoading";
import InfiniteScroll from "../InfiniteScroll";
import { SortableList } from "../SortableList";
import styles from "./index.module.less";

export default function DraggableBoardList({
  hideButton = false,
  hideUser,
  hideTime,
  dataSource,
  type,
  onLoadMore,
  onDelete,
  hasMore,
  onEnd,
  draggable,
  children,
  onBoardCardRender,
  endMessage = undefined,
}) {
  const [displayInfo, setDisplayInfo] = useState(null);
  const { column, headerHeight } = useAppStore(({ column, headerHeight }) => ({
    column,
    headerHeight,
  }));
  const [deleteMap, setDeleteMap] = useState({});
  const containerRef = useRef(null);
  const intervalRef = useRef(null);

  const onDeleteBoard = useCallback(
    (boardId) => {
      setDeleteMap((deleteMap) => {
        return {
          ...deleteMap,
          [boardId]: true,
        };
      });
      onDelete(boardId);
    },
    [onDelete]
  );

  const onSortEnd = useCallback(
    (oldIndex, newIndex) => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }

      oldIndex !== newIndex && onEnd({ newIndex, oldIndex });
    },
    [onEnd]
  );

  // const { value: boardCardType } = useABTest("board_card", "feed-old");

  useEffect(() => {
    const onScroll = throttle(() => {
      if (!containerRef.current) return;
      const rect = containerRef.current.getBoundingClientRect();
      const top = rect?.top - headerHeight;
      const height = document.body.clientHeight - headerHeight;
      const boardCardEl = containerRef.current.querySelector(".board-card");

      const itemHeight = boardCardEl?.clientHeight;
      const row = Math.ceil(height / itemHeight) + 2; // 加多一行
      const num = top > 0 ? 0 : Math.floor(-top / itemHeight);

      setDisplayInfo({
        min: Math.max((num - 1) * column, 0),
        max: num * column + row * column,
      });
    }, 300);

    onScroll();

    window.addEventListener("scroll", onScroll);

    return () => {
      window.removeEventListener("scroll", onScroll);
    };
  }, [headerHeight, column, type]);

  const list = useMemo(() => {
    return dataSource.filter((record) => !deleteMap[record.board_id]);
  }, [dataSource, deleteMap]);

  return (
    <div className={styles.container} ref={containerRef}>
      <InfiniteScroll
        dataLength={dataSource.length}
        hasMore={hasMore}
        next={onLoadMore}
        loader={
          <div className={classNames([styles.loading, "loading"])}>
            <HBLoading size="30px" />
          </div>
        }
        endMessage={
          endMessage === undefined ? (
            <p
              data-gd-expose="resource_loc_expose"
              data-resource-title="没有更多了"
              data-resource-type="文字"
              className={classNames([styles.noMore, "noMore"])}
            >
              没有更多了
            </p>
          ) : (
            endMessage
          )
        }
        scrollThreshold="800px"
        style={{
          overflowX: "hidden",
          marginRight: -32,
        }}
      >
        <div className={styles.list}>
          <SortableList
            items={list}
            getItemId={(item) => item.board_id}
            isDragDisabled={(item) => !draggable || item.is_private === 2}
            onSortEnd={onSortEnd}
            // onSortStart={onSortStart}
            // onSortCancel={onSortCancel}
            autoScroll={true}
            renderItem={({
              item,
              isDragging,
              index,
              ref,
              props,
              handleProps,
            }) => {
              const sortIndex = index + (children ? 1 : 0);

              // 占位块，视窗外部分用占位块填充，用以提升性能
              if (
                !isDragging &&
                (sortIndex < displayInfo?.min || sortIndex > displayInfo?.max)
              ) {
                return (
                  <div
                    key={item.board_id}
                    ref={ref}
                    // {...props}
                    {...handleProps}
                    style={handleProps}
                    className={classNames([
                      styles.placeholder,
                      type === "list" ? styles.small : null,
                    ])}
                  ></div>
                );
              }

              onBoardCardRender && onBoardCardRender(item, index);

              return (
                <BoardCard
                  key={item.board_id}
                  record={item}
                  hideButton={hideButton}
                  hideUser={hideUser}
                  hideFollowCount={false}
                  hideTime={hideTime}
                  type={type}
                  draggable={draggable}
                  onDelete={onDeleteBoard}
                  isDragging={isDragging}
                  boardType="new"
                  domRef={ref}
                  {...props}
                  {...handleProps}
                />
              );
            }}
          >
            {children}
          </SortableList>
        </div>
        {/* <SortableList
          dataSource={list}
          distance={1}
          axis="xy"
          helperClass={classNames([
            styles.helper,
            type === "list" ? styles.small : null,
          ])}
          onSortEnd={onSortEnd}
          onSortMove={onSortMove}
          disableAutoscroll={true}
          column={column}
          displayInfo={displayInfo}
          draggable={draggable}
          cardProps={{
            hideButton,
            hideUser,
            type,
            draggable,
            onDelete: onDeleteBoard,
          }}
        >
          {children}
        </SortableList> */}
      </InfiniteScroll>
    </div>
  );
}
